import { ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  ButtonGroup,
  Input as ChakraInput,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconMagicWand } from 'central-icons/IconMagicWand';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { useIsRevelioAdmin } from '@revelio/auth';
import { LoadingBox } from '@revelio/core';
import { Button, Option, Text } from '@revelio/ui';

import { AlertDialog } from '../../../shared/components/alert-dialog';
import { verifyDataBuilderPayload } from '../api/data-builder-api';
import type { DataBuilderPayload } from '../api/data-builder-api.types';
import { extractDataBuilderErrorMessage } from '../api/data-builder-error-message';
import {
  type JsonLintIssue,
  parseVerifyValidationIssues,
  validateDataBuilderJson,
} from '../api/data-builder-json-lint';
import { DataBuilderHttpError } from '../api/errors';
import { hydrateStoreFromPayload } from '../api/hydrate-store-from-payload';
import { useDatasetStorage } from '../api/useDatasetStorage';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderGrid } from '../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../components/ui/DataBuilderOption';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import { dataBuilderStore } from '../data-builder.store';
import modalStyles from '../data-builder-modals.module.css';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import { JsonRequestEditor } from './JsonRequestEditor';
import styles from './step1-saved-datasets.module.css';

// Seed the paste editor focused on deliverables — the only field the admin
// fills in here. It's valid JSON, so the editor opens without a lint error.
const PASTE_TEMPLATE = `{
  "deliverables": []
}`;

// client / name / settings are defaulted on verify and load so the admin can
// focus on deliverables. client/name must be non-empty — the backend rejects
// empty strings ("String should have at least 1 character"). A field the admin
// includes in the paste overrides its default (shallow merge — their value wins).
const PASTE_DEFAULTS = {
  client: 'Untitled',
  name: 'Untitled',
  settings: { lag_data: 0 },
};

const withPasteDefaults = (parsed: unknown): unknown =>
  parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)
    ? parsed
    : { ...PASTE_DEFAULTS, ...(parsed as Record<string, unknown>) };

// Translate a failed /verify into editor state. Returns true if it surfaced a
// backend validation error (structured inline marks or a plain-text message),
// false for a transient/non-HTTP failure the caller should handle itself.
const applyVerifyFailure = (
  error: unknown,
  setServerDiagnostics: (issues: JsonLintIssue[]) => void,
  setVerifyError: (message: string | null) => void
): boolean => {
  const issues = parseVerifyValidationIssues(error);
  if (issues.length > 0) {
    setServerDiagnostics(issues);
    setVerifyError(null);
    return true;
  }
  if (error instanceof DataBuilderHttpError) {
    setServerDiagnostics([]);
    setVerifyError(
      extractDataBuilderErrorMessage(error, 'This request failed validation.')
    );
    return true;
  }
  return false;
};

const formatSavedDatasetDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const DataBuilderSavedDatasetsStep = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { goToDatasetStep } = useDataBuilderNavigation();
  const {
    listDatasets,
    updateDataset,
    deleteDataset,
    canManageDatasets,
    datasetsQueryKey,
  } = useDatasetStorage();
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const { data: datasets = [], isLoading } = useQuery({
    queryKey: datasetsQueryKey,
    queryFn: listDatasets,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isPasteOpen, setIsPasteOpen] = useState(false);
  const [pasteValue, setPasteValue] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [serverDiagnostics, setServerDiagnostics] = useState<JsonLintIssue[]>(
    []
  );
  const [isVerifying, setIsVerifying] = useState(false);
  // A backend validation error that isn't per-field (plain-text or otherwise
  // unstructured), so it can't become an inline mark — shown as a message.
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const latestPasteValueRef = useRef(pasteValue);
  latestPasteValueRef.current = pasteValue;

  // Validate pasted content against the backend as the admin edits so issues
  // surface early — the load itself re-verifies and blocks on failure.
  // Debounced, and skipped for the untouched template and for unparseable JSON
  // (which the editor's own parse linter already flags).
  useEffect(() => {
    if (!isPasteOpen || pasteValue === PASTE_TEMPLATE) {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(pasteValue);
    } catch {
      return;
    }

    const verifiedText = pasteValue;
    const handle = setTimeout(() => {
      setIsVerifying(true);
      verifyDataBuilderPayload(withPasteDefaults(parsed) as DataBuilderPayload)
        .then(() => {
          if (latestPasteValueRef.current === verifiedText) {
            setServerDiagnostics([]);
            setVerifyError(null);
          }
        })
        .catch((error: unknown) => {
          if (latestPasteValueRef.current !== verifiedText) {
            return;
          }
          // Network/transient failures stay quiet mid-typing; the load re-checks.
          if (
            !applyVerifyFailure(error, setServerDiagnostics, setVerifyError)
          ) {
            setServerDiagnostics([]);
            setVerifyError(null);
          }
        })
        .finally(() => {
          if (latestPasteValueRef.current === verifiedText) {
            setIsVerifying(false);
          }
        });
    }, 600);

    return () => clearTimeout(handle);
  }, [pasteValue, isPasteOpen]);

  const selectedDataset =
    selectedId === null
      ? undefined
      : datasets.find((dataset) => dataset.id === selectedId);
  const editingDataset =
    editingId === null
      ? undefined
      : datasets.find((dataset) => dataset.id === editingId);

  const hydrateAndGoToDataset = (
    payload: DataBuilderPayload,
    loadedSavedDatasetId: string | null
  ) => {
    const hydratedState = hydrateStoreFromPayload(payload);
    // Reset first so in-session state from a previously loaded dataset
    // (acknowledged edit warnings, in-flight mapper requests, persist errors)
    // doesn't leak into this load — matching DataBuilderShareRedeem and
    // DataBuilderStatus, which both reset before hydrating.
    dataBuilderStore.getState().reset();
    dataBuilderStore.setState({
      ...hydratedState,
      loadedSavedDatasetId,
    });
    goToDatasetStep();
  };

  const handleContinue = () => {
    if (selectedDataset === undefined) {
      return;
    }

    hydrateAndGoToDataset(selectedDataset.payload, selectedDataset.id);
  };

  const handleOpenPaste = () => {
    setPasteValue(PASTE_TEMPLATE);
    setPasteError(null);
    setServerDiagnostics([]);
    setVerifyError(null);
    setIsVerifying(false);
    setIsSubmitting(false);
    setIsPasteOpen(true);
  };

  const handleClosePaste = () => {
    setIsPasteOpen(false);
    setPasteValue('');
    setPasteError(null);
    setServerDiagnostics([]);
    setVerifyError(null);
    setIsVerifying(false);
    setIsSubmitting(false);
  };

  const handleLoadPastedRequest = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(pasteValue);
    } catch {
      setServerDiagnostics([]);
      setVerifyError(null);
      setPasteError('Could not parse JSON. Check the request and try again.');
      return;
    }

    setPasteError(null);
    setIsSubmitting(true);

    // Verify before loading: an invalid payload crashes the builder downstream,
    // so a failed /verify must block the load — the admin fixes it first.
    try {
      await verifyDataBuilderPayload(
        withPasteDefaults(parsed) as DataBuilderPayload
      );
    } catch (error: unknown) {
      if (!applyVerifyFailure(error, setServerDiagnostics, setVerifyError)) {
        setServerDiagnostics([]);
        setVerifyError(
          "Couldn't reach validation. Check your connection and try again."
        );
      }
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);

    try {
      hydrateAndGoToDataset(
        withPasteDefaults(parsed) as DataBuilderPayload,
        null
      );
    } catch {
      setPasteError(
        'Could not load this request. Check the request and try again.'
      );
    }
  };

  const handleFormatPaste = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(pasteValue);
    } catch {
      setPasteError('Could not parse JSON. Check the request and try again.');
      return;
    }

    setPasteValue(JSON.stringify(parsed, null, 2));
    setPasteError(null);
    setServerDiagnostics([]);
    setVerifyError(null);
  };

  const handleEditStart = (id: string, currentName: string) => {
    setEditingId(id);
    setEditingName(currentName);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRename = async (id: string) => {
    const dataset = datasets.find((item) => item.id === id);
    const nextName = editingName.trim();
    if (dataset === undefined || nextName.length === 0) {
      return;
    }

    setSavingId(id);
    try {
      await updateDataset({
        id: dataset.id,
        name: nextName,
        payload: dataset.payload,
      });
      await queryClient.invalidateQueries({ queryKey: datasetsQueryKey });
      handleEditCancel();
      // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    } catch (error: unknown) {
      toast({
        status: 'error',
        title: 'Could not rename saved dataset',
        description: 'Please try again.',
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await deleteDataset(id);
      await queryClient.invalidateQueries({ queryKey: datasetsQueryKey });
      if (selectedId === id) {
        setSelectedId(null);
      }
      // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
    } catch (error: unknown) {
      toast({
        status: 'error',
        title: 'Could not delete saved dataset',
        description: 'Please try again.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRenameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (editingId === null) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      void handleRename(editingId);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleEditCancel();
    }
  };

  const renderPasteStatus = () => {
    if (pasteError !== null) {
      return (
        <Text
          fontSize="sm"
          className={styles.pasteError}
          data-testid="saved-datasets-paste-error"
        >
          {pasteError}
        </Text>
      );
    }
    if (isVerifying) {
      return (
        <Box className={styles.pasteCheckingRow}>
          <Spinner size="xs" />
          <Text fontSize="sm" className={styles.pasteChecking}>
            Checking request…
          </Text>
        </Box>
      );
    }
    if (serverDiagnostics.length > 0) {
      return (
        <Text
          fontSize="sm"
          className={styles.pasteWarning}
          data-testid="saved-datasets-paste-verify-status"
        >
          {serverDiagnostics.length} validation{' '}
          {serverDiagnostics.length === 1 ? 'issue' : 'issues'} found —
          highlighted above. Fix to load.
        </Text>
      );
    }
    if (verifyError !== null) {
      return (
        <Text
          fontSize="sm"
          className={styles.pasteWarning}
          data-testid="saved-datasets-paste-verify-status"
        >
          {verifyError}
        </Text>
      );
    }
    return null;
  };

  const hasDatasets = datasets.length > 0;

  return (
    <DataBuilderStepLayout
      title="Select Saved Datasets"
      footer={
        <DataBuilderFooter
          actions={
            isRevelioAdmin ? (
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button
                  size="sm"
                  color="primary"
                  disabled={selectedDataset === undefined}
                  onClick={handleContinue}
                >
                  Continue
                </Button>
                <Menu placement="top-end">
                  <MenuButton
                    as={IconButton}
                    className={styles.splitMenuButton}
                    data-testid="saved-datasets-continue-menu-button"
                    aria-label="More dataset options"
                    icon={<ChevronUpIcon />}
                    colorScheme="green"
                    variant="solid"
                    size="sm"
                  />
                  <MenuList className={styles.menuList}>
                    <MenuItem
                      data-testid="saved-datasets-paste-request"
                      onClick={handleOpenPaste}
                    >
                      Paste in a request (JSON)
                    </MenuItem>
                  </MenuList>
                </Menu>
              </ButtonGroup>
            ) : (
              <Button
                size="sm"
                color="primary"
                disabled={selectedDataset === undefined}
                onClick={handleContinue}
              >
                Continue
              </Button>
            )
          }
        />
      }
    >
      <LoadingBox loading={isLoading}>
        <DataBuilderGrid columns="2" style={{ paddingBottom: '12px' }}>
          {hasDatasets &&
            datasets.map((dataset) => {
              const isRenaming = savingId === dataset.id;
              const isDeleting = deletingId === dataset.id;
              const isSelected = selectedId === dataset.id;
              const formattedUpdatedAt = formatSavedDatasetDate(
                dataset.updatedAt
              );

              return (
                <Option
                  key={dataset.id}
                  data-testid={`saved-dataset-option-${dataset.id}`}
                  style={{ minHeight: '64px', alignItems: 'flex-start' }}
                  isSelected={isSelected}
                  onClick={() => setSelectedId(dataset.id)}
                  startSlot={
                    <DataBuilderOption.Radio isSelected={isSelected} />
                  }
                  startSlotAlignment="center"
                  contentAlignment="start"
                  endSlot={
                    canManageDatasets ? (
                      <Menu placement="bottom-end" offset={[0, 2]}>
                        <MenuButton
                          as={IconButton}
                          className={styles.cardMenuButton}
                          icon={<IconDotGrid1x3Vertical size={14} />}
                          aria-label="Saved dataset actions"
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          borderRadius="4px"
                          onClick={(event) => event.stopPropagation()}
                          data-testid={`saved-dataset-actions-${dataset.id}`}
                          isLoading={isRenaming || isDeleting}
                        />
                        <MenuList
                          className={styles.menuList}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MenuItem
                            data-testid={`saved-dataset-rename-${dataset.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleEditStart(dataset.id, dataset.name);
                            }}
                            isDisabled={isRenaming || isDeleting}
                          >
                            Rename
                          </MenuItem>
                          <MenuItem
                            data-testid={`saved-dataset-delete-${dataset.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              setConfirmDeleteId(dataset.id);
                            }}
                            isDisabled={isDeleting}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    ) : undefined
                  }
                >
                  <div>
                    <DataBuilderOption.Label>
                      {dataset.name}
                    </DataBuilderOption.Label>
                    {formattedUpdatedAt && (
                      <DataBuilderOption.Description>
                        {`Last updated ${formattedUpdatedAt}`}
                      </DataBuilderOption.Description>
                    )}
                  </div>
                </Option>
              );
            })}
          {!hasDatasets && <Text fontSize="md">No saved datasets found.</Text>}
        </DataBuilderGrid>
      </LoadingBox>
      <Modal
        isOpen={editingDataset !== undefined}
        onClose={handleEditCancel}
        isCentered
      >
        <ModalOverlay />
        <ModalContent className={modalStyles.modalContent}>
          <ModalHeader color="text.primary">Rename Saved Dataset?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ChakraInput
              className={modalStyles.modalInput}
              value={editingName}
              onChange={(event) => setEditingName(event.target.value)}
              onKeyDown={handleRenameKeyDown}
              placeholder="Enter a dataset name"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              style={{ marginRight: '12px' }}
              onClick={handleEditCancel}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => {
                if (editingId !== null) {
                  void handleRename(editingId);
                }
              }}
              disabled={
                editingName.trim().length === 0 ||
                (editingId !== null && savingId === editingId)
              }
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isPasteOpen}
        onClose={handleClosePaste}
        isCentered
        size="xl"
      >
        <ModalOverlay />
        <ModalContent className={modalStyles.modalContent}>
          <ModalHeader color="text.primary">
            Paste a Data Builder Request
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" style={{ marginBottom: '8px' }}>
              Paste a request payload (JSON). It hydrates the builder and
              continues to dataset selection.
            </Text>
            <Box position="relative">
              <Tooltip label="Format JSON" placement="top">
                <IconButton
                  aria-label="Format JSON"
                  icon={<IconMagicWand size={16} />}
                  onClick={handleFormatPaste}
                  isDisabled={pasteValue.trim().length === 0}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  position="absolute"
                  top="6px"
                  right="6px"
                  zIndex={2}
                  bg="whiteAlpha.800"
                  data-testid="saved-datasets-paste-format"
                />
              </Tooltip>
              <JsonRequestEditor
                value={pasteValue}
                onChange={(next) => {
                  setPasteValue(next);
                  if (pasteError !== null) {
                    setPasteError(null);
                  }
                  if (serverDiagnostics.length > 0) {
                    setServerDiagnostics([]);
                  }
                  if (verifyError !== null) {
                    setVerifyError(null);
                  }
                }}
                validate={validateDataBuilderJson}
                serverDiagnostics={serverDiagnostics}
              />
            </Box>
            {renderPasteStatus()}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={handleClosePaste}
              style={{ marginRight: 'auto' }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => {
                void handleLoadPastedRequest();
              }}
              disabled={pasteValue.trim().length === 0 || isSubmitting}
              leftIcon={isSubmitting ? <Spinner size="xs" /> : undefined}
              data-testid="saved-datasets-paste-load"
            >
              {isSubmitting ? 'Verifying…' : 'Load Request'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {confirmDeleteId !== null ? (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          onClose={() => setConfirmDeleteId(null)}
          headerText="Want to Delete This Saved Dataset?"
          bodyContent={
            <span>
              {datasets.find((dataset) => dataset.id === confirmDeleteId)?.name}{' '}
              will be permanently removed. Any previous runs will still be
              available.
            </span>
          }
          footerAcceptIsDestructive
          footerAcceptText="Yes, Delete Dataset"
          onAccept={() => {
            void handleDelete(confirmDeleteId);
          }}
        />
      ) : null}
    </DataBuilderStepLayout>
  );
};
